import { useState, useEffect, useMemo } from 'react';
import {
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getTournamentEvents, deleteEvent } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import EventModal from '../../../components/EventModal';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

interface EventData {
  id: string;
  title: string;
  date: string;
  time?: string;
  location?: string;
  description?: string;
  type?: string;
}

function toLocalDateStr(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

const typeColors: Record<string, string> = {
  Registration: 'bg-blue-100 text-blue-700',
  Tournament: 'bg-golf-orange/10 text-golf-orange',
  Ceremony: 'bg-amber-100 text-amber-700',
  Social: 'bg-green-100 text-green-700',
};

export default function MobileAdminCalendar() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const today = new Date();

  const [currentDate, setCurrentDate] = useState(today);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventData | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<EventData | null>(null);

  useEffect(() => {
    if (tournamentId) fetchEvents();
  }, [tournamentId]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data, error } = await getTournamentEvents(tournamentId);
      if (error) throw error;
      const sorted = (data || []).sort((a: EventData, b: EventData) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setEvents(sorted);

      if (sorted.length > 0) {
        const todayStr = toLocalDateStr(new Date());
        const hasTodayEvents = sorted.some((e: EventData) => e.date === todayStr);
        if (!hasTodayEvents) {
          const future = sorted.map((e: EventData) => e.date).filter((d: string) => d >= todayStr).sort();
          const past = sorted.map((e: EventData) => e.date).filter((d: string) => d < todayStr).sort().reverse();
          const nearest = future[0] || past[0];
          if (nearest) {
            const [y, m, d] = nearest.split('-').map(Number);
            const nearestDate = new Date(y, m - 1, d);
            setCurrentDate(nearestDate);
            setCalendarMonth(new Date(y, m - 1, 1));
          }
        }
      }
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEvent) return;
    try {
      const { error } = await deleteEvent(deletingEvent.id);
      if (error) throw error;
      toast.success('Event deleted');
      setDeletingEvent(null);
      fetchEvents();
    } catch {
      toast.error('Failed to delete event');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const eventDates = useMemo(() => new Set(events.map(e => e.date)), [events]);

  const monthCalendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  }, [calendarMonth]);

  const selectedDateStr = toLocalDateStr(currentDate);
  const dayEvents = events.filter(e => e.date === selectedDateStr);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (!tournamentId) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] px-6 text-center">
        <CalendarDaysIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">No tournament selected</p>
        <p className="text-gray-300 text-xs mt-1">Select a tournament to manage calendar events</p>
      </div>
    );
  }

  return (
    <div className="px-4 pt-2 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500">{events.length} total events</p>
        </div>
        <button
          onClick={() => { setEditingEvent(null); setIsModalOpen(true); }}
          className="bg-golf-orange text-white rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm font-medium shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Add Event
        </button>
      </div>

      {/* Mini calendar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-4 p-3">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 text-gray-400" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-golf-gray">
              {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => {
                setCurrentDate(new Date());
                setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
              }}
              className="px-2 py-0.5 text-[10px] font-medium rounded border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
            >
              Today
            </button>
          </div>
          <button
            onClick={() => setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-7 mb-0.5">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{day}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {monthCalendarDays.map((date, i) => {
            if (!date) return <div key={`empty-${i}`} />;
            const dateStr = toLocalDateStr(date);
            const hasEvents = eventDates.has(dateStr);
            const isSelected = isSameDay(date, currentDate);
            const isToday = isSameDay(date, today);
            return (
              <button
                key={i}
                onClick={() => setCurrentDate(date)}
                className={`relative flex flex-col items-center justify-center rounded-lg text-xs font-medium transition-all py-1.5 ${
                  isSelected
                    ? 'bg-golf-orange text-white shadow-sm'
                    : isToday
                    ? 'bg-orange-50 text-golf-orange border border-golf-orange/40'
                    : hasEvents
                    ? 'text-gray-800 font-semibold hover:bg-gray-50'
                    : 'text-gray-400 hover:bg-gray-50'
                }`}
              >
                {date.getDate()}
                {hasEvents && (
                  <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-golf-orange'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day label + add shortcut */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
        <button
          onClick={() => { setEditingEvent(null); setIsModalOpen(true); }}
          className="text-xs text-golf-orange font-medium flex items-center gap-1 hover:opacity-70 transition-opacity"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Add
        </button>
      </div>

      {/* Events for selected day */}
      {dayEvents.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <CalendarDaysIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No events on this day</p>
          <button
            onClick={() => { setEditingEvent(null); setIsModalOpen(true); }}
            className="mt-3 text-xs text-golf-orange font-medium underline"
          >
            Add an event
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {dayEvents.map(event => {
            let timeLabel = '';
            if (event.time) {
              try {
                timeLabel = format(parseISO(`2000-01-01T${event.time}`), 'h:mm a');
              } catch {
                timeLabel = event.time;
              }
            }
            return (
              <div key={event.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {event.type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium mb-1.5 inline-block ${typeColors[event.type] || 'bg-gray-100 text-gray-600'}`}>
                        {event.type}
                      </span>
                    )}
                    <p className="font-semibold text-golf-gray text-sm">{event.title}</p>
                    {event.description && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                      {event.time && (
                        <span className="flex items-center gap-1 text-xs text-golf-orange font-medium">
                          <ClockIcon className="w-3.5 h-3.5" />
                          {timeLabel || event.time}
                        </span>
                      )}
                      {event.location && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <MapPinIcon className="w-3.5 h-3.5" />
                          {event.location}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditingEvent(event); setIsModalOpen(true); }}
                      className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeletingEvent(event)}
                      className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tournamentId && (
        <EventModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          tournamentId={tournamentId}
          event={editingEvent}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingEvent}
        onClose={() => setDeletingEvent(null)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${deletingEvent?.title}"?`}
      />
    </div>
  );
}
